import React, { ComponentType, useEffect } from "react";
import { useRouteError } from "react-router";
import * as Sentry from "@sentry/react";

const messages = {
    cs: {
        chunk: {
            title: "Je dostupná nová verze",
            body: "Pro pokračování obnovte stránku.",
        },
        generic: {
            title: "Něco se pokazilo",
            body: "Zkuste prosím obnovit stránku.",
        },
        reload: "Obnovit",
    },
    en: {
        chunk: {
            title: "A new version is available",
            body: "Please reload the page to continue.",
        },
        generic: {
            title: "Something went wrong",
            body: "Please try reloading the page.",
        },
        reload: "Reload",
    },
};

function getMessages() {
    const lang = navigator.language?.split("-")[0];
    return lang === "cs" ? messages.cs : messages.en;
}

function isChunkLoadError(error: unknown): boolean {
    return (
        error instanceof Error &&
        (error.name === "ChunkLoadError" ||
            error.message.includes("Loading chunk") ||
            error.message.includes("Loading CSS chunk"))
    );
}

export function RouteErrorElement() {
    const error = useRouteError();
    const chunkError = isChunkLoadError(error);

    useEffect(() => {
        if (!chunkError) Sentry.captureException(error);
    }, [chunkError, error]);

    const t = getMessages();
    const { title, body } = chunkError ? t.chunk : t.generic;
    return (
        <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-surface p-4 text-center">
            <h2 className="text-xl font-semibold text-ink">{title}</h2>
            <p className="text-sm text-ink-muted">{body}</p>
            <button
                type="button"
                onClick={() => window.location.reload()}
                className="flex cursor-pointer appearance-none items-center gap-2 rounded-lg! border-0 bg-accent px-4 py-2 text-sm font-medium text-white! transition-colors hover:brightness-110"
            >
                {t.reload}
            </button>
        </div>
    );
}

const MAX_RETRIES = 3;

export function lazyWithRetry<T extends ComponentType<unknown>>(
    importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
    return React.lazy(() => {
        let attempts = 0;

        const load = (): Promise<{ default: T }> =>
            importFn().catch(() => {
                attempts++;

                if (attempts >= MAX_RETRIES) {
                    // Final attempt failed — force reload to get new manifest
                    window.location.reload();
                    // Return a never-resolving promise to prevent rendering stale UI
                    return new Promise(() => {});
                }

                // Wait before retrying (1s, 2s)
                return new Promise<{ default: T }>((resolve) =>
                    setTimeout(() => resolve(load()), attempts * 1000),
                );
            });

        return load();
    });
}
